import { useCallback } from "react";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_UserInformation } from "../../interfaces/userInformation.interface";
import { useRequest } from "../useRequest.hook";
import { FetchOptions } from "../../interfaces/fetchOptions.interface";
import { DA_UserAccountDetails } from "../../interfaces/userAccountDetails.interface";

type GetUserInfo = (options?: FetchOptions) => Promise<DA_UserInformation>;

type SetUserInfo = (
    parameters: DA_UserInformation,
    options?: FetchOptions
) => Promise<DA_UserInformation>;

type GetUserAccountDetails = (
    customerNumber: string,
    options?: FetchOptions
) => Promise<DA_UserAccountDetails>;

type GetEditProfileLink = (
    locale: string,
    redirectUri: string,
    options?: FetchOptions
) => Promise<{ url: string }>;

type GetResetPasswordLink = (
    locale: string,
    redirectUri: string,
    options?: FetchOptions
) => Promise<{ url: string }>;

export const useUserInformationApi = () => {
    const request = useRequest();
    const { currentSite } = useAppData();

    const getUserInfo: GetUserInfo = useCallback(
        (options?) => {
            const path = currentSite === "B2C" ? "/api/UserInfo" : "user/userinfo";
            return request.get(path, undefined, options);
        },
        [currentSite, request]
    );

    const setUserInfo: SetUserInfo = useCallback(
        (parameters, options?) => {
            const path = currentSite === "B2C" ? "/api/UserInfo/userinfo" : "user/userinfo";
            return request.put(path, parameters, options).then((response) => response.json());
        },

        [currentSite, request]
    );

    const getUserAccountDetails: GetUserAccountDetails = useCallback(
        (customerNumber, options?) =>
            request.get(`/user/getuseraccountdetails/${customerNumber}`, options),
        [request]
    );

    const getEditProfileLink: GetEditProfileLink = useCallback(
        (locale, redirectUri, options?) =>
            request.get(
                `utility/editprofilelink`,
                {
                    locale,
                    redirecturi: encodeURIComponent(redirectUri),
                },
                options
            ),
        [request]
    );

    const getResetPasswordLink: GetResetPasswordLink = useCallback(
        (locale, redirectUri, options?) => {
            const path =
                currentSite === "B2C"
                    ? "/api/UserInfo/resetpasswordlink"
                    : "utility/resetpasswordlink";
            return request.get(
                path,
                {
                    locale,
                    redirecturi: encodeURIComponent(redirectUri),
                },
                options
            );
        },
        [currentSite, request]
    );

    return {
        getUserInfo,
        setUserInfo,
        getUserAccountDetails,
        getEditProfileLink,
        getResetPasswordLink,
    };
};
